import { usePermissions } from "@/hooks/usePermissions";
import LocationService from "@/services/locations";
import { useAuth0 } from "@auth0/auth0-react";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import SearchIcon from "@mui/icons-material/Search";
import TuneIcon from "@mui/icons-material/Tune";
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Divider,
  FormControl,
  FormControlLabel,
  FormGroup,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { CustomCheckboxIcon, CustomCheckboxIconChecked } from '../common/custom-checkbox';
import { useAppSelector } from "@/store/hooks";
import {
  selectSelectedGDM,
  selectSelectedSiteId,
  selectSelectedSiteName,
} from "@/store/selectors";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { Dayjs } from "dayjs";
import type { LocationOption } from "@/types/locations";
import { formatDate } from "@/utils/date";

const TYPE_OPTIONS = [
  { key: "Report", labelKey: "typeReport" },
  { key: "Manual", labelKey: "typeManual" },
  { key: "Drawing", labelKey: "typeDrawing" },
  { key: "Certificate", labelKey: "typeCertificate" },
  { key: "Other", labelKey: "typeOther" },
];

interface SiteDocumentationHeaderProps {
  filterDocumentation?: (filters: any) => void;
  paginationModel: { page: number; pageSize: number };
  onPaginationModelChange: (
    model: { page: number; pageSize: number },
    details?: any
  ) => void;
  onCreateDocumentationClick?: () => void;
}

const SiteDocumentationHeader = React.memo(
  ({
    filterDocumentation,
    paginationModel,
    onPaginationModelChange,
    onCreateDocumentationClick,
  }: SiteDocumentationHeaderProps) => {
    const { t } = useTranslation();
    const { getAccessTokenSilently } = useAuth0();
    const { getFeatureActions } = usePermissions();

    const selectedSiteId = useAppSelector(selectSelectedSiteId);
    const siteName = useAppSelector(selectSelectedSiteName);
    const totalCount = useAppSelector(
      (state) => state.siteDocumentation.totalCount
    );
    const { data: userData } = useAppSelector((state) => state.user);
    const guid = useAppSelector(selectSelectedGDM);

    const showgdmnumber = getFeatureActions(
      "Navigation",
      "View Customer GDM ID",
      "Customer Navigation"
    );
    const [search, setSearch] = useState("");
    const [showTypeDropdown, setShowTypeDropdown] = useState(false);
    const [showReportDateDropdown, setShowReportDateDropdown] = useState(false);
    const [showUploadDateDropdown, setShowUploadDateDropdown] = useState(false);
    const [selectedTypeOptions, setSelectedTypeOptions] = useState<string[]>(
      []
    );

    const [filtersOpen, setFiltersOpen] = useState(false);
    const [filterCount, setFilterCount] = useState(0);
    const [filtersChanged, setFiltersChanged] = useState(false);
    const [reportDate, setReportDate] = useState<Dayjs | null>(null);
    const [uploadDate, setUploadDate] = useState<Dayjs | null>(null);

    // Location dropdown state
    const [showLocationDropdown, setShowLocationDropdown] = useState(false);
    const [selectedLocations, setSelectedLocations] = useState<string[]>([]);
    const [locations, setLocations] = useState<LocationOption[]>([]);
    const [locationsLoading, setLocationsLoading] = useState(false);

    // Flatten locations helper function
    const flattenLocations = (
      locations: LocationOption[]
    ): LocationOption[] => {
      const result: LocationOption[] = [];
      function recurse(items: LocationOption[]) {
        for (const item of items) {
          result.push(item);
          if (Array.isArray(item.children) && item.children.length > 0) {
            recurse(item.children);
          }
        }
      }
      recurse(locations);
      return result;
    };

    // Fetch locations from API
    useEffect(() => {
      const fetchLocations = async () => {
        if (!selectedSiteId) {
          setLocations([]);
          return;
        }
        setLocationsLoading(true);
        try {
          const accessToken = await getAccessTokenSilently();
          const locationService = new LocationService(
            userData?.id || "",
            guid || "",
            undefined,
            accessToken
          );
          const res = await locationService.getAllByGdmNumber();
          const locationData: LocationOption[] = res?.results || [];
          setLocations(flattenLocations(locationData));
        } catch (err) {
          console.error("Error fetching locations:", err);
          setLocations([]);
        } finally {
          setLocationsLoading(false);
        }
      };
      fetchLocations();
    }, [selectedSiteId, getAccessTokenSilently]);

    // Debounce filter update on search
    const debounceTimeout = useRef<NodeJS.Timeout | null>(null);
    useEffect(() => {
      if (debounceTimeout.current) {
        clearTimeout(debounceTimeout.current);
      }
      debounceTimeout.current = setTimeout(() => {
        triggerFilterUpdate();
      }, 400); // 400ms debounce
      return () => {
        if (debounceTimeout.current) {
          clearTimeout(debounceTimeout.current);
        }
      };
    }, [search]);

    useEffect(() => {
      if (
        selectedTypeOptions.length > 0 ||
        selectedLocations.length > 0 ||
        reportDate ||
        uploadDate
      ) {
        setFiltersChanged(true);
      } else {
        setFiltersChanged(false);
      }
    }, [selectedTypeOptions, selectedLocations, reportDate, uploadDate]);

    const triggerFilterUpdate = () => {
      if (filterDocumentation) {
        const filters = {
          search,
          type: selectedTypeOptions,
          location: selectedLocations,
          reportDate: reportDate ? reportDate.format("YYYY-MM-DD") : null,
          uploadDate: uploadDate ? uploadDate.format("YYYY-MM-DD") : null,
        };
        filterDocumentation(filters);
        setFiltersChanged(false);
      }
      updateAndLogFilters();
    };

    useEffect(() => {
      // Update filter count based on selected filters
      const dateCount = (reportDate ? 1 : 0) + (uploadDate ? 1 : 0);
      const count =
        selectedTypeOptions.length + selectedLocations.length + dateCount;
      setFilterCount(count);
    }, [selectedTypeOptions, selectedLocations, reportDate, uploadDate]);

    // Helper for updating and logging filter object
    const updateAndLogFilters = () => {
      setShowTypeDropdown(false);
      setShowReportDateDropdown(false);
      setShowUploadDateDropdown(false);
      setShowLocationDropdown(false);
    };

    return (
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <Box className="custom-modal">
          <Box className="device-header-container">
            <Box>
              <Typography variant="h3" className="page-title" sx={{
    mt: 0,       
    pt: 0,      
  }}>
                {t("siteDocumentation", "Site Documentation")}
              </Typography>
            
            </Box>
            {/* Add Documentation Button */}
            <Button
              startIcon={<AddIcon />}
              variant="text"
              onClick={onCreateDocumentationClick}
              className="add-device-button"
            >
              {t("addDocumentation", "ADD DOCUMENTATION")}
            </Button>
          </Box>
        </Box>
      </LocalizationProvider>
    );
  }
);

export default SiteDocumentationHeader;
